import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, Inject, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getEnumLookup } from 'src/app/_helpers/enum-helper';
import { SampleCensorSign, SampleCensorType } from 'src/app/enums/sampleCensorType.enum';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import { MassUnit } from 'src/app/enums/unitPreferences.enum';
import { Units, UnitSymbols } from 'src/app/enums/units.enum';
import { UnsampledTimeCorrectionType } from 'src/app/enums/unsampledTimeCorrectionType.enum';
import { IhLabResult } from 'src/app/models/IhLabResult.model';
import { Lookup } from 'src/app/models/lookup.model';
import { AgentService } from 'src/app/services/agent.service';
import { IhLabResultService } from 'src/app/services/IH-lab-result.service';
import { JurisdictionService } from 'src/app/services/jurisdiction.service';
import { OelTypeService } from 'src/app/services/oel-type.service';
import { SampleService } from 'src/app/services/sample.service';
import { ToastService } from 'src/app/services/toast.service';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import nameOf from 'src/app/shared/form/nameOf.helper';

export interface ILabResultDialogData extends IFormDialogData {
  sampleType: SampleTypeEnum;
}

interface GroupedRisk {
  totalMass: number;
  blankCorrectedMass: number | null;
  concentration: number;
  unsampledConcentration: number;
  isTwa: boolean;
  unitSymbol: string;
  massUnit: number;
  dosePercentage?: number;
  sampleCensor: number;
  sampleCensorSign: string;
  twa: number;
  unit: Units;
  results: IhLabResult[];
}

@Component({
  standalone: false,
  selector: 'app-lab-results-dialog',
  templateUrl: './lab-results-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/form/form.component.scss', './lab-results-dialog.component.scss']
})
export class LabResultsDialogComponent implements OnInit, OnDestroy {
  iHLabResults: IhLabResult[] = [];
  agents: Lookup[];
  jurisdictions: Lookup[];
  oelTypes: Lookup[];
  groupedRisks: GroupedRisk[];
  private readonly destroy$ = new Subject<void>();
  baseDisplayedColumns: string[] = [
    nameOf<IhLabResult>(x => x.agentId),
    nameOf<IhLabResult>(x => x.jurisdictionId),
    nameOf<IhLabResult>(x => x.oelTypeId),
    nameOf<IhLabResult>(x => x.sampleCensor),
    nameOf<IhLabResult>(x => x.totalMass),
    nameOf<IhLabResult>(x => x.concValue),
    nameOf<IhLabResult>(x => x.concentration),
    nameOf<IhLabResult>(x => x.unsampledTimeConcValue),
    'twaResult',
    'edit'
  ];
  displayedColumns: string[] = [...this.baseDisplayedColumns];
  unsampledTimeCorrectionTypeEnum = UnsampledTimeCorrectionType;
  isUpdated = false;
  sensorList: Lookup[] = [];
  unitList: Lookup[] = getEnumLookup(MassUnit);
  unitSymbols = UnitSymbols;
  selectedUnit: Units = Units.UgPerM3;
  massUnits = MassUnit;
  sampleTypeEnum = SampleTypeEnum;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ILabResultDialogData,
    public dialogRef: MatDialogRef<LabResultsDialogComponent>,
    private sampleService: SampleService,
    private agentService: AgentService,
    private jurisdictionService: JurisdictionService,
    private oelTypeService: OelTypeService,
    private ihLabResultService: IhLabResultService,
    private toastService: ToastService,
    private valueConversionService: ValueConversionService
  ) { }

  ngOnInit(): void {
    this.getLabresults();
    if (this.data.sampleType === SampleTypeEnum.Noise) {
      this.displayedColumns = [
        nameOf<IhLabResult>(x => x.agentId),
        nameOf<IhLabResult>(x => x.jurisdictionId),
        nameOf<IhLabResult>(x => x.oelTypeId),
        nameOf<IhLabResult>(x => x.dosePercentage),
        nameOf<IhLabResult>(x => x.twa),
        'edit'
      ];
      this.selectedUnit = Units.Db;
    } else if (this.data.sampleType === SampleTypeEnum.Wipe) {
      this.displayedColumns = [
        nameOf<IhLabResult>(x => x.agentId),
        nameOf<IhLabResult>(x => x.sampleCensor),
        nameOf<IhLabResult>(x => x.totalMass),
        'twaResult',
        'edit'
      ];
      this.selectedUnit = Units.UgPerCm2;
    } else if (this.data.sampleType === SampleTypeEnum.Biological) {
      this.displayedColumns = [
        nameOf<IhLabResult>(x => x.agentId),
        nameOf<IhLabResult>(x => x.jurisdictionId),
        nameOf<IhLabResult>(x => x.oelTypeId),
        nameOf<IhLabResult>(x => x.sampleCensor),
        nameOf<IhLabResult>(x => x.concentration),
        'twaResult',
        'edit'
      ];
      this.selectedUnit = Units.UgPerDl;
    }
    this.sensorList = getEnumLookup(SampleCensorSign).filter(res => res.id != SampleCensorType.NA);
  }

  getTwaUnitSymbol(group: GroupedRisk): string {
    if (this.data.sampleType === SampleTypeEnum.Wipe && group?.unit) {
      return `${UnitSymbols[group.unit]}`;
    }
    return group.unitSymbol || '-';
  }

  getTwaButtonLabel(): string {
    if (this.data.sampleType === SampleTypeEnum.Biological) return 'Save Result';
    return this.data.sampleType === SampleTypeEnum.Wipe ? 'Calculate TWA' : 'Compute TWA';
  }

  getLabresults() {
    forkJoin({
      labResults: this.sampleService.getLabResults(this.data.id),
      agents: this.agents ? of(this.agents) : this.agentService.getAll(),
      jurisdictions: this.jurisdictions ? of(this.jurisdictions) : this.jurisdictionService.getAll(),
      oelTypes: this.oelTypes ? of(this.oelTypes) : this.oelTypeService.getAll(),
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe(res => {
      this.iHLabResults = res.labResults;
      this.agents = res.agents;
      this.jurisdictions = res.jurisdictions;
      this.oelTypes = res.oelTypes;
      this.updateBlankCorrectedMassColumn(this.iHLabResults);
      if (this.iHLabResults.length) this.groupedRisks = this.groupByAgent(this.iHLabResults);
    })
  }

  computeTWA(group: GroupedRisk, currRow: IhLabResult | null = null) {
    const results = currRow ? group.results.filter(r => currRow.jurisdictionId === r.jurisdictionId) : group.results;
    const massInUg = group.totalMass != null
        ? (group.massUnit === 1 ? group.totalMass * 1000 : group.totalMass)
        : null;
    const apiCalls = results.map(model => {
      const samplePayload = {
        sampleId: model.sampleId,
        jurisdictionId: model.jurisdictionId,
        oelTypeId: model.oelTypeId,
        agentId: model.agentId,
      }
      const noisePayload = { dosePercent: currRow?.dosePercentage, sampleCensor: SampleCensorType.Equal };
      const wipePayload = {
        sampleCensor: group.sampleCensor,
        totalMass: massInUg,
        unsampledConcentration: group.unsampledConcentration != null ? this.valueConversionService.convertAgentUnitValueToBase(group.unsampledConcentration, group.unit) : null,
        twa: group.results && group.results.length > 0 && group.results[0].wipeArea != null && massInUg != null
          ? massInUg / group.results[0].wipeArea
           : null,
      }
      const concPayload = {
        sampleCensor: group.sampleCensor,
        concentration: group.concentration ? this.valueConversionService.convertAgentUnitValueToBase(group.concentration, group.unit) : null,
        totalMass: group.totalMass != null && !group.concentration ? massInUg : null,
        unsampledConcentration: group.unsampledConcentration != null ? this.valueConversionService.convertAgentUnitValueToBase(group.unsampledConcentration, group.unit) : null
      }
      let payload;
      if (this.data.sampleType === SampleTypeEnum.Noise) {
        payload = { ...samplePayload, ...noisePayload };
      } else if (this.data.sampleType === SampleTypeEnum.Wipe) {
        payload = { ...samplePayload, ...wipePayload };
      } else {
        payload = { ...samplePayload, ...concPayload };
      }
      return this.ihLabResultService.createManualLabResult(payload);
    });

    forkJoin(apiCalls).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result) => {
        group.isTwa = true;
        if (result[0]) {
          if (this.data.sampleType === SampleTypeEnum.Wipe) {
            group.twa = group.unit === Units.UgPerFt2 ? this.valueConversionService.convertUgPerCm2ToUgPerFt2(result[0].twa) : result[0].twa;
            group.totalMass = group.massUnit === 1 ? result[0].totalMass / 1000 : result[0].totalMass;
          } else {
            group.twa = this.valueConversionService.getParameterValueAsPerAgentUnit(result[0].twa, group.unit);
            group.totalMass = group.massUnit === 1 ? result[0].totalMass / 1000 : result[0].totalMass;
          }
          if (currRow) {
            currRow.twa = group.twa;
          }
          group.concentration = this.valueConversionService.getParameterValueAsPerAgentUnit(result[0].concValue, group.unit);
          group.unsampledConcentration = this.valueConversionService.getParameterValueAsPerAgentUnit(result[0].unsampledTimeConcValue, group.unit);
          // Noise samples always use Equal censor; other types fall back to Equal when missing
          const effectiveCensor = this.data.sampleType === SampleTypeEnum.Noise
            ? SampleCensorType.Equal
            : (result[0]?.sampleCensor != null ? result[0].sampleCensor : SampleCensorType.Equal);
          group.sampleCensor = effectiveCensor;
          group.sampleCensorSign = effectiveCensor !== SampleCensorType.NA
            ? SampleCensorSign[effectiveCensor]
            : '';
        }
        this.toastService.showSuccess('Lab result updated successfully', true);
        this.isUpdated = true;
      },
      error: (error) => {
        this.toastService.showError(error);
      }
    })
  }

  private updateBlankCorrectedMassColumn(results: IhLabResult[]): void {
    // Blank correction only applies to chemical samples (not Noise)
    if (this.data.sampleType === SampleTypeEnum.Noise) return;

    const hasBlankCorrection = results.some(r => r.blankCorrectionApplied);
    const blankColKey = nameOf<IhLabResult>(x => x.blankCorrectedMass);
    const alreadyHasColumn = this.displayedColumns.includes(blankColKey);

    if (hasBlankCorrection && !alreadyHasColumn) {
      const totalMassIndex = this.displayedColumns.indexOf(nameOf<IhLabResult>(x => x.totalMass));
      if (totalMassIndex === -1) return;
      this.displayedColumns.splice(totalMassIndex + 1, 0, blankColKey);
    } else if (!hasBlankCorrection && alreadyHasColumn) {
      this.displayedColumns = this.displayedColumns.filter(col => col !== blankColKey);
    }
  }

  private groupByAgent(results: IhLabResult[]) {
    const activeResults = results.filter(r => !r.isDeleted);
    const groups: { [key: string]: IhLabResult[] } = {};

    activeResults.forEach(result => {
      const key = result.agentId ?? 'none';
      result.agentName = this.agents.find(agent => agent.id === result.agentId)?.name;
      result.jurisdictionName = this.jurisdictions.find(jurisdiction => jurisdiction.id === result.jurisdictionId)?.name;
      result.oelTypeName = this.oelTypes.find(oeltypes => oeltypes.id === result.oelTypeId)?.name;

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(result);
    });

    return Object.keys(groups).map(key => {
      const first = groups[key][0];
      const groupUnit = first?.agentUnits || this.selectedUnit;
      const isWipe = this.data.sampleType === SampleTypeEnum.Wipe;
      const isNoise = this.data.sampleType === SampleTypeEnum.Noise;
      // Noise samples always use Equal censor; other types fall back to Equal when missing
      const effectiveCensor = isNoise ? SampleCensorType.Equal : (first.sampleCensor != null ? first.sampleCensor : SampleCensorType.Equal);
      return {
        totalMass: isWipe ? first.totalMass : this.valueConversionService.getParameterValueAsPerAgentUnit(first.totalMass, groupUnit),
        blankCorrectedMass: first.blankCorrectedMass != null
          ? (isWipe ? first.blankCorrectedMass : this.valueConversionService.getParameterValueAsPerAgentUnit(first.blankCorrectedMass, groupUnit))
          : null,
        sampleCensor: effectiveCensor,
        sampleCensorSign: effectiveCensor !== SampleCensorType.NA
          ? SampleCensorSign[effectiveCensor]
          : '',
        concentration: this.valueConversionService.getParameterValueAsPerAgentUnit(first.concValue, groupUnit),
        unsampledConcentration: first.unsampledTimeConcValue === 1 ? first.concValue : this.valueConversionService.getParameterValueAsPerAgentUnit(first.unsampledTimeConcValue, groupUnit),
        twa: isWipe && groupUnit === Units.UgPerFt2 ? this.valueConversionService.convertUgPerCm2ToUgPerFt2(first.twa) : isWipe ? first.twa : this.valueConversionService.getParameterValueAsPerAgentUnit(first.twa, groupUnit),
        dosePercentage: first.dosePercentage,
        isTwa: first.twa != null ? true : false,
        unit: groupUnit,
        unitSymbol: UnitSymbols[groupUnit],
        massUnit: isWipe ? 0 : (groupUnit === Units.MgPerM3 ? 1 : 0),
        results: groups[key]
      };
    });
  }

  public trackById = trackById;
  public trackByIndex = trackByIndex;

  allowUpdate(group: GroupedRisk) {
    group.isTwa = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close() {
    this.dialogRef.close(this.isUpdated);
  }
}
